import React, { useState } from 'react';
import { LanguageSwitcher } from '../shared';
import '../questionnaire/GDPRConsentModal.css';

interface RegistrationGDPRConsentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConsentGiven: (consent: boolean) => void;
  language?: 'en' | 'el'; // Add language prop
}

const RegistrationGDPRConsentModal: React.FC<RegistrationGDPRConsentModalProps> = ({
  isOpen,
  onClose,
  onConsentGiven,
  language
}) => {
  const [selectedLanguage, setSelectedLanguage] = useState<'en' | 'el'>(language || 'el'); // Use prop language or default to Greek

  const handleLanguageChange = (language: 'en' | 'el') => {
    setSelectedLanguage(language);
  };

  const handleConsent = (consent: boolean) => {
    onConsentGiven(consent);
  };

  if (!isOpen) return null;

  const content = {
    en: {
      title: "Data Protection & Privacy Consent",
      description: "Before you begin this patient registration form, we need your informed consent for data processing in accordance with GDPR regulations.",
      details: [
        "Your personal and medical information will be used exclusively for patient registration and healthcare provision purposes.",
        "All data will be stored securely and confidentially in our medical database system.",
        "Only authorized medical personnel and administrative staff will have access to your information.",
        "Your data will be used to create your patient profile and facilitate your medical care.",
        "You have the right to withdraw your consent at any time by contacting your doctor or our clinic.",
        "If you have any questions or concerns about data processing, please discuss them with your doctor before proceeding.",
        "If you decline consent, you will not be able to complete the registration form and will be redirected to an information page."
      ],
      accept: "I Accept and Proceed",
      decline: "I Decline"
    },
    el: {
      title: "Συναίνεση Προστασίας & Απορρήτου Δεδομένων",
      description: "Πριν ξεκινήσετε αυτή τη φόρμα εγγραφής ασθενούς, χρειαζόμαστε την ενημερωμένη συναίνεσή σας για την επεξεργασία δεδομένων σύμφωνα με τους κανόνες GDPR.",
      details: [
        "Οι προσωπικές και ιατρικές σας πληροφορίες θα χρησιμοποιηθούν αποκλειστικά για σκοπούς εγγραφής ασθενούς και παροχής υγειονομικής περίθαλψης.",
        "Όλα τα δεδομένα θα αποθηκευτούν με ασφάλεια και εμπιστευτικότητα στο ιατρικό μας σύστημα βάσης δεδομένων.",
        "Μόνο εξουσιοδοτημένο ιατρικό προσωπικό και διοικητικό προσωπικό θα έχει πρόσβαση στις πληροφορίες σας.",
        "Τα δεδομένα σας θα χρησιμοποιηθούν για τη δημιουργία του προφίλ ασθενούς σας και τη διευκόλυνση της ιατρικής σας περίθαλψης.",
        "Έχετε το δικαίωμα να αποσύρετε τη συναίνεσή σας οποιαδήποτε στιγμή επικοινωνώντας με τον γιατρό σας ή την κλινική μας.",
        "Εάν έχετε οποιεσδήποτε ερωτήσεις ή ανησυχίες σχετικά με την επεξεργασία δεδομένων, παρακαλώ συζητήστε τις με τον γιατρό σας πριν προχωρήσετε.",
        "Εάν δεν δώσετε τη συναίνεσή σας, δεν θα μπορείτε να συμπληρώσετε τη φόρμα εγγραφής και θα μεταφερθείτε σε μια σελίδα πληροφοριών."
      ],
      accept: "Αποδέχομαι και Συνεχίζω",
      decline: "Δεν Αποδέχομαι"
    }
  };

  const t = content[selectedLanguage as keyof typeof content];

  return (
    <div className="gdpr-modal-overlay">
      <div className="gdpr-modal">
        <div className="gdpr-modal-header">
          <h2>{t.title}</h2>
          <LanguageSwitcher
            selectedLanguage={selectedLanguage}
            onLanguageChange={handleLanguageChange}
            variant="default"
          />
        </div>
        
        <div className="gdpr-modal-content">
          <p className="gdpr-description">{t.description}</p>
          <div className="gdpr-details">
            <h6>{selectedLanguage === 'el' ? 'Σημαντικές Πληροφορίες:' : 'Important Information:'}</h6>
            <ul>
              {t.details.map((detail, index) => (
                <li key={index}>{detail}</li>
              ))}
            </ul>
          </div>
        </div>
        
        <div className="gdpr-modal-actions">
          <button 
            className="gdpr-button decline" 
            onClick={() => handleConsent(false)}
          >
            {t.decline}
          </button>
          <button 
            className="gdpr-button accept" 
            onClick={() => handleConsent(true)}
          >
            {t.accept}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegistrationGDPRConsentModal;